import { tryFunctionHelperModule } from '../../helpers/try-function';
import { intercomServiceModule } from '../intercom-service/intercom-service';
import * as StringUtils from '../../utils/string-utils';
import { appConfig } from '../../constants/app-config';
import { getCookie } from '../../utils/cookie-utils';
import { utmProps } from '../../utils/utm-tracking-utils/utm-tracking-utils';
import { getAccountService } from '../../utils/account-utils/account-utils';

export function getImpersonateId() {
  return getCookie('impersonator_id');
}

export const trackerServiceModule = angular
  .module('Q.services.tracker-service', [
    tryFunctionHelperModule,
    intercomServiceModule,
  ])
  .factory(
    'tracker',
    function ($window, intercomService, $injector, tryFunction, $log) {
      // assign the appname as a custom session variable
      if ($window.ga) {
        $window.ga('set', 'dimension1', appConfig.appName);
      }

      const service = {
        shouldTrack,

        setUser: tryFunction((user) => {
          if (shouldTrack()) {
            if ($window.analytics) {
              if (user.user) {
                user = user.user;
              }

              service.user = user;

              let userData = {};

              if (user.team) {
                $window.analytics.group(user.teamId, user.team);
                // some analytics services don't respond to analytics.group, so track the key team properties as fields
                userData = {
                  customer: {
                    id: user.team.customerId,
                    name: user.team.customer && user.team.customer.name,
                    state: user.team.customer && user.team.customer.state,
                    trialEndedAt:
                      user.team.customer && user.team.customer.trialEndedAt,
                  },
                  team: {
                    name: user.team.name,
                    primary: user.team.primaryTeam,
                    // legacy trait
                    subscriptionState:
                      user.team.customer && user.team.customer.state,
                    state: user.team.state,
                  },
                };
              }

              $window.analytics.identify(
                user.id,
                angular.extend({}, user, userData),
                setContext(),
              );
            }

            // Implement fullstory set user here (Datadog rum replacement)

            intercomService.setUser(angular.extend({}, user, utmProps));
          }
        }),
        unsetUser: tryFunction(() => {
          service.user = null;

          if ($window.analytics) {
            $window.analytics.reset();
          }
          if ($window.mixpanel && $window.mixpanel.cookie) {
            $window.mixpanel.cookie.clear();
          }
          // Implement fullstory clear user here (Datadog rum replacement)

          intercomService.unsetUser();
        }),
        trackPage: tryFunction((name, props, options) => {
          props = props || {};
          props.app = appConfig.appName;
          props.env = appConfig.env;

          if ($window.analytics && shouldTrack()) {
            $window.analytics.page(name, props, setContext(options));
          }
          intercomService.trackPage();
        }),
        trackEvent: tryFunction((name, properties, options = {}) => {
          if (appConfig.devMode) {
            $log.debug('trackEvent:', name, properties, options);
          }

          if (shouldTrack()) {
            const $state = $injector.has('$state') && $injector.get('$state');
            // always include these basic properties
            const props = angular.extend(
              {
                app: appConfig.appName,
                env: appConfig.env,
                ui: {},
              },
              properties,
            );
            props.ui.$state = $state && $state.current && $state.current.name;

            if ($window.analytics) {
              $window.analytics.track(name, props, setContext(options));
            } else if ($window.ga) {
              const props = properties || {};
              $window.ga('send', 'event', {
                eventCategory: appConfig.appName,
                eventAction: name,
                // try to guess which key--if any--might work as the event label
                eventLabel:
                  props[
                    ['name', 'title', 'state', 'label', 'message', 'id'].find(
                      (k) => props[k],
                    )
                  ],
                eventValue: !isNaN(props.value) ? props.value : null,
              });
            }
            // Implement fullstory add action here (Datadog rum replacement)

            if (options.intercom !== false) {
              intercomService.trackEvent(name, properties);
            }
          }
        }),
        trackError: tryFunction((name, properties, options = {}) => {
          $log.error(name, properties);
          service.trackEvent('Error: ' + name, properties, options);
        }),
        // extended version of trackEvent, which takes a model instance. If the properties are provided as a
        // function, the result of that function will be be returned. If no properties are provided, then
        // the properties will be derived from the model instance.
        trackModelEvent: tryFunction((instance, name, properties) => {
          if (!instance) return;
          // if track properties were specifically configured then use those instead
          if (properties) {
            if (angular.isFunction(properties)) {
              properties = properties(instance);
            }
            // if the instance support a trackProperties method, then use that as the default. Otherwise
            // just use key fields from the instance itself.
          } else {
            const defaults = {
              id: instance.id,
              $type: instance.$type,
              name: instance.name,
              title: instance.title,
              candidate: instance.candidate
                ? instance.candidate.email
                : undefined,
              team: instance.team ? instance.team.name : undefined,
            };
            properties = instance.trackProperties
              ? instance.trackProperties(defaults)
              : defaults;
          }
          service.trackEvent(name, properties, { intercom: false });
        }),
      };

      function shouldTrack() {
        let accountService = getAccountService($injector);
        let account = accountService && accountService.get();

        // If there is a current user
        if (account.user) {
          return (
            account.verified && // must be verified in the service
            account.user.role !== 'staff' && // must not be a staff member
            !account.user.demoAccount && // must not be a demo account
            (account.user.$type !== 'Candidate' ||
              !account.user.isActive ||
              account.user.isActive()) && // must not be an inactive candidate
            !getImpersonateId()
          ); // must not be impersonating this user
        }
        // otherwise we do not currently track anonymous users
        return false;
      }

      function setContext(options) {
        options = options || {};
        // by default segment only passes along utm values if they are in the current url.
        // We are going to pull them from the cookie so that everything gets tracked back to the source referrer.
        options.context = {
          campaign: {
            name: utmProps.utm_campaign,
            source: utmProps.utm_source,
            medium: utmProps.utm_medium,
            term: utmProps.utm_term,
            content: utmProps.utm_content,
          },
        };

        return options;
      }

      return service;
    },
  )
  .run(function (tracker, $rootScope, $injector, tryFunction) {
    let rebroadcastEvents = ['app.accountSet', 'app.login', 'app.logout'];

    rebroadcastEvents.forEach((event) => {
      $rootScope.$on(event, (e, args) => {
        if (tracker.shouldTrack()) {
          $rootScope.$broadcast(`tracker.${event}`, args);
        }
      });
    });

    $rootScope.$on(
      'model.actionEndpoint.success',
      tryFunction((e, args) => {
        let track = args.options.track;
        if (track) {
          if (angular.isString(track)) {
            track = { event: track };
          }

          if (track === true) {
            track = { event: true };
          } else if (!track.event) {
            track.event = true;
          }

          if (track.event === true) {
            const verb = StringUtils.upperCaseWords(
              StringUtils.pastTense(args.name),
            );
            track.event = `${args.model.name} ${verb}`;
          }

          tracker.trackModelEvent(args.instance, track.event, track.properties);
        }
      }),
    );

    // handles model that are configured to automatically track their CUD events
    $rootScope.$on(
      'model.changed',
      tryFunction((e, args) => {
        const track =
          args.config.meta &&
          args.config.meta.track &&
          args.config.meta.track[args.action];
        if (track) {
          let event = args.action + 'd';
          if (event === 'destroyd') event = 'destroyed';
          event = StringUtils.upperCaseWords(StringUtils.pastTense(event));
          tracker.trackModelEvent(
            args.instance,
            `${args.resourceName} ${event}`,
            track === true ? null : track,
          );
        }
      }),
    );

    // collect the previous state on the ui.router $stateChangeStart
    // because the nestedPageTitleHelper pulls the current title
    let previousState;
    const stateDelimiter = ':';

    $rootScope.$on('tracker.app.accountSet', (e, account) =>
      tracker.setUser(account),
    );
    $rootScope.$on('tracker.app.logout', () => tracker.unsetUser());

    $rootScope.$on(
      '$stateChangeStart',
      tryFunction((event, toState, toParams, fromState) => {
        // default to tracking state changes unless the
        // route explicitly sets track to false
        if (fromState.track !== false) {
          const fromPageTitle =
            fromState && fromState.data && fromState.data.pageTitle;
          if (fromPageTitle && !angular.isString(fromPageTitle)) {
            previousState = $injector.invoke(fromPageTitle);
            if (angular.isArray(previousState)) {
              previousState = previousState.join(stateDelimiter);
            }
          } else {
            previousState = '';
          }
        } else {
          previousState = '';
        }
      }),
    );

    $rootScope.$on(
      '$stateChangeSuccess',
      tryFunction((event, toState, toParams, fromState) => {
        // these events are noisy as hell so lets not track them in the development environment and save
        // ourselves some money
        // default to tracking state changes unless the
        // route explicitly sets track to false
        if (toState.track !== false && toState.name !== 'error') {
          let toPageTitle = toState.data && toState.data.pageTitle;
          if (toPageTitle && !angular.isString(toPageTitle)) {
            toPageTitle = $injector.invoke(toPageTitle);
            if (angular.isArray(toPageTitle)) {
              toPageTitle = toPageTitle.join(stateDelimiter);
            }
          }

          const props = {
            title: toPageTitle,
            fromStateName: previousState || fromState.name || '',
            toParams: toParams,
          };

          // srefProps are set by the ui-sref directive, which utilize the triggering
          // element's track-data attribute.
          angular.merge(props, tracker.srefProps);
          tracker.srefProps = null;

          // turn mixpanel off in dev mode
          const options = {
            mixpanel: appConfig.env !== 'dev',
          };

          let pageName = toState.data && toState.data.label;
          if (!pageName && toPageTitle) {
            pageName = toPageTitle.split(':')[0];
          }

          tracker.trackPage(pageName || toState.name, props, options);
        }
      }),
    );
  });
